import React, { useCallback, useEffect, useRef, useState } from 'react';
import html2pdf from 'html2pdf.js';
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  FormCheck,
  Row,
  Spinner,
} from 'react-bootstrap';
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { Feature } from '@lomarengas/design-system';
import { Helmet } from 'react-helmet';
import DatePicker from 'react-datepicker';
import { format } from 'date-fns-tz';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { useAuthUser } from '../auth/authHooks';
import OwnerQuickSearch, {
  OwnerOptionType,
} from '../../common/components/OwnerQuickSearch/OwnerQuickSearch';
import CabinQuickSearchFieldOnly, {
  AccommodationOptionType,
} from '../../common/components/CabinQuickSearch/CabinQuickSearchFieldOnly';
import {
  SearchBarFilters,
  ActionButtons,
  addStatusToast,
  DataTableItem,
  Loader,
  SectionHeader,
} from '../../common/components';

import {
  AccommodationQueryFilters,
  getAllAccommodationsAsync,
  selectAccommodations,
  selectAccommodationsLoadingStatus,
  selectAccommodationsPagination,
} from '../accommodations/accommodationsSearchSlice';

import { InitialPaginationParameters } from '../../common/types/pagination.types';
import { LOADING_STATUS } from '../../common/constants';
import {
  getSettlementReport,
  selectSettlementReport,
  selectSettlementReportLoadingStatus,
  SettlementReportItem,
  SettlementReportItemLine,
  SettlementReportRequest,
} from './settlementReportSlice';
import { dateToDateOnlyString } from '../../common/utils/dateUtils';
import {
  BookingType,
  bookingTypeTranslations,
} from '../bookings/Constants/BookingTypeEnum';

interface DateRange {
  from: Date;
  to: Date;
}

interface SettlementReportVatItem {
  vatP: number;
  vat: number;
  SumWithVat: number;
}

interface SettlementReportVats {
  settlementReportVatItems: SettlementReportVatItem[];
}

function SettlementReport() {
  const settlementReportVats: SettlementReportVats = {
    settlementReportVatItems: [],
  };
  function getBookingTypeTranslation(
    bookingType: BookingType,
    locale: 'en' | 'fi',
  ): string {
    return bookingTypeTranslations[locale][bookingType] || 'Tuntematon';
  }
  const contentRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const [searchParams, setSearchParams] = useSearchParams();
  const accommodationDropdownSelectedAccommodationFromParam = searchParams.get(
    'accommodationDropdownSelectedAccommodation',
  );
  const ownersAccommodationIdFromParam = searchParams.get('accommodationId');

  const dispatch = useAppDispatch();
  const [
    accommodationDropdownSelectedAccommodation,
    setAccommodationDropdownSelectedAccommodation,
  ] = useState<string>(
    accommodationDropdownSelectedAccommodationFromParam != null
      ? accommodationDropdownSelectedAccommodationFromParam
      : undefined,
  );
  const noBookingsAfterEndDateFromParams = searchParams.get(
    'noBookingsAfterEndDate',
  );
  const [noBookingsAfterEndDate, setNoBookingsAfterEndDate] = useState(
    noBookingsAfterEndDateFromParams === 'true',
  );
  const [selectedOwnerOptionType, setSelectedOwnerOptionType] =
    useState<OwnerOptionType | null>(null);
  const [selectedAccommodationOptionType, setSelectedAccommodationOptionType] =
    useState<AccommodationOptionType | null>(null);
  let today = new Date();
  let oneMonthAgo = new Date();
  oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
  // Create a date object for tomorrow
  // const tomorrow = new Date();
  // tomorrow.setDate(today.getDate() + 1);
  const [dayPickerSelectedRange, setDayPickerSelectedRange] =
    useState<DateRange>({
      from: oneMonthAgo,
      to: today,
    });
  const [startDate, setStartDate] = useState(oneMonthAgo);
  const [endDate, setEndDate] = useState(today);
  const bookingsStartDateFromParams = searchParams.get('bookingsStartDate');
  const bookingsEndDateFromParams = searchParams.get('bookingsEndDate');

  if (bookingsStartDateFromParams != null) {
    oneMonthAgo = new Date(bookingsStartDateFromParams);
  }
  if (bookingsEndDateFromParams != null) {
    today = new Date(bookingsEndDateFromParams);
  }

  const ownersAccommodations = useAppSelector(selectAccommodations);
  const settlementReport = useAppSelector(selectSettlementReport);

  const settlementReportLoadingStatus = useAppSelector(
    selectSettlementReportLoadingStatus,
  );
  const ownersAccommodationsLoadingStatus = useAppSelector(
    selectAccommodationsLoadingStatus,
  );
  const ownersAccommodationsPagination = useAppSelector(
    selectAccommodationsPagination,
  );
  const [accommodationFiltering, setAccommodationFiltering] =
    useState<AccommodationQueryFilters>({
      PageNumber: InitialPaginationParameters.PageNumber,
      PageSize: InitialPaginationParameters.PageSize,
      filterCriteria: {
        showDeleted: false,
        searchString: undefined,
        qualityRatings: [],
        accommodationNumber: undefined,
        novelty: undefined,
        onSale: undefined,
        showInOwnerPage: undefined,
        accommodationGroupId: undefined,
        accountId: undefined,
      },
    });

  const handleSelectOwnerChange = (ownerOption: OwnerOptionType | null) => {
    setSelectedOwnerOptionType(ownerOption);
    const paginatedAccommodationFiltering = accommodationFiltering;
    paginatedAccommodationFiltering.PageNumber = 1;
    paginatedAccommodationFiltering.filterCriteria.accountId =
      ownerOption.value;
    dispatch(getAllAccommodationsAsync(paginatedAccommodationFiltering));
    /*
    setFeedbacksFilter({
      ...feedbacksFilter,
      ownerId: ownerOption ? ownerOption.value : null,
    });
     */
  };
  const accommodationDropdownChanged = (
    e: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    console.log(e.target.value);
    if (e.target.value !== '0') {
      const selectedAccommodation = ownersAccommodations.find(
        accommodation => accommodation.id === e.target.value,
      );
      setAccommodationDropdownSelectedAccommodation(selectedAccommodation.id);
    } else {
      setAccommodationDropdownSelectedAccommodation(undefined);
    }
  };
  const setAccommodationDropdownDateRange = (
    startDateSelected: Date,
    endDateSelected: Date,
  ) => {
    const selectedRange = dayPickerSelectedRange;
    if (startDateSelected !== undefined) {
      selectedRange.from = startDateSelected;
      setStartDate(startDateSelected);
      // Move enddate, if selected startdate is greater than endDate
      if (endDate < startDateSelected) {
        setEndDate(startDateSelected);
      }
    }
    if (endDateSelected !== undefined) {
      selectedRange.to = endDateSelected;
      setEndDate(endDateSelected);
    }
    setDayPickerSelectedRange(selectedRange);
  };

  const formattedAmountWithEuroCurrency = (amount: number) => {
    return new Intl.NumberFormat('fi-FI', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };
  const doSearch = () => {
    const reportRequest: SettlementReportRequest = {
      ownerId: selectedOwnerOptionType.value,
      bookingsStartDate: startDate,
      bookingsEndDate: endDate,
      isOwnerUser: true,
      noBookingsAfterEndDate,
    };
    searchParams.set(
      'bookingsStartDate',
      format(reportRequest.bookingsStartDate, 'yyyy-MM-dd'),
    );
    searchParams.set(
      'bookingsEndDate',
      format(reportRequest.bookingsEndDate, 'yyyy-MM-dd'),
    );
    searchParams.set(
      'noBookingsAfterEndDate',
      reportRequest.noBookingsAfterEndDate.toString(),
    );
    if (accommodationDropdownSelectedAccommodation != null) {
      reportRequest.accommodationId =
        accommodationDropdownSelectedAccommodation;
      searchParams.set(
        'accommodationDropdownSelectedAccommodation',
        accommodationDropdownSelectedAccommodation,
      );
    } else {
      searchParams.delete('accommodationDropdownSelectedAccommodation');
    }
    setSearchParams(searchParams);
    dispatch(getSettlementReport(reportRequest));
  };
  const createLinkToBooking = (reportLine: SettlementReportItemLine) => {
    const link = `/bookings/${reportLine.bookingId}?bookingNumber=${
      reportLine.bookingNumber
    }&accommodationId=${reportLine.accommodationId}`;
    return link;
  };
  const noBookingsAfterEndDateHandler = () => {
    setNoBookingsAfterEndDate(!noBookingsAfterEndDate);
  };
  let totalSettlementValue = 0;
  const renderReportLine = (
    reportLine: SettlementReportItemLine,
    extraBooking: boolean,
  ) => {
    if (reportLine.ownerPurchasePriceVatP > 0) {
      const existingVatItem =
        settlementReportVats.settlementReportVatItems.find(
          item => item.vatP === reportLine.ownerPurchasePriceVatP,
        );
      if (existingVatItem) {
        // If it exists, add to the existing item's prices
        existingVatItem.vat += reportLine.ownerPurchasePriceVat; // Add your value here
        existingVatItem.SumWithVat += reportLine.ownerPurchasePrice; // Add your value here
      } else {
        // If it doesn't exist, add a new item with zero prices
        settlementReportVats.settlementReportVatItems.push({
          vatP: reportLine.ownerPurchasePriceVatP,
          vat: reportLine.ownerPurchasePriceVat,
          SumWithVat: reportLine.ownerPurchasePrice,
        });
      }
    }
    totalSettlementValue += reportLine.ownerPurchasePrice;
    return (
      <tr key={reportLine.bookingId + reportLine.productName}>
        <td>
          {extraBooking && '* '}
          {extraBooking && reportLine.bookingNumber}
          {reportLine.isMainProduct && (
            <>
              <div className="bookingNumberLink">
                <Link to={createLinkToBooking(reportLine)}>
                  {' '}
                  {reportLine.bookingNumber}
                </Link>
              </div>
              <div className="bookingNumberText" style={{ display: 'none' }}>
                {reportLine.bookingNumber}{' '}
              </div>
            </>
          )}
        </td>
        <td>
          {!reportLine.isMainProduct && '+ '}{' '}
          {`${reportLine.productNumber} ${reportLine.productName}`}
        </td>
        <td>{dateToDateOnlyString(reportLine.startDate)}</td>
        <td>{dateToDateOnlyString(reportLine.endDate)}</td>
        <td>
          {reportLine.isMainProduct &&
            reportLine.bookingType === 5 &&
            getBookingTypeTranslation(reportLine.bookingType, 'fi')}
          {reportLine.isMainProduct &&
            !extraBooking &&
            reportLine.payerFirstName}{' '}
          {reportLine.isMainProduct &&
            !extraBooking &&
            reportLine.payerLastName}{' '}
          <br />
          {reportLine.isMainProduct &&
            !extraBooking &&
            reportLine.payerPhoneNumber}{' '}
          {reportLine.isMainProduct && !extraBooking && reportLine.payerEmail}
        </td>
        <td>
          {formattedAmountWithEuroCurrency(reportLine.ownerPurchasePrice)}
        </td>
        <td>{reportLine.ownerPurchasePriceVatP} %</td>
        <td>
          {formattedAmountWithEuroCurrency(reportLine.ownerPurchasePriceVat)}
        </td>
      </tr>
    );
  };

  const renderReportItem = (
    reportItem: SettlementReportItem,
    extraBooking: boolean,
  ) => {
    const bookingAdditionalNotes =
      reportItem?.settlementReportlines?.[0]?.payerNotes ?? null;
    return (
      <>
        {/* Render the main report lines */}
        {reportItem.settlementReportlines.map(reportLine =>
          renderReportLine(reportLine, extraBooking),
        )}
        {bookingAdditionalNotes != null && bookingAdditionalNotes && (
          <tr>
            <td colSpan={8}>
              {extraBooking && '* '}Lisätieto: {bookingAdditionalNotes}
            </td>
          </tr>
        )}
        {/* Render the extra booking report items recursively */}
        {reportItem.extraBookingSettlementReportItems?.map(
          (extraReportItem, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <React.Fragment key={index}>
              {renderReportItem(extraReportItem, true)}
            </React.Fragment>
          ),
        )}
        {!extraBooking && (
          <tr>
            <td colSpan={8}>
              <hr />
            </td>
          </tr>
        )}
      </>
    );
  };

  const renderReport = () => {
    settlementReportVats.settlementReportVatItems = [];
    return settlementReport.settlementReportItems.map((reportItem, index) => (
      // eslint-disable-next-line react/no-array-index-key
      <React.Fragment key={index}>
        {renderReportItem(reportItem, false)}
      </React.Fragment>
    ));
  };
  const renderVats = () => {
    return settlementReportVats.settlementReportVatItems.map(
      (vatItem, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <tr key={index}>
          <td className="px-8">{vatItem.vatP} %</td>
          <td>
            {formattedAmountWithEuroCurrency(vatItem.SumWithVat - vatItem.vat)}
          </td>
          <td className="text-nowrap">
            {formattedAmountWithEuroCurrency(vatItem.vat)}
          </td>
          <td>{formattedAmountWithEuroCurrency(vatItem.SumWithVat)}</td>
        </tr>
      ),
    );
  };
  const showOrHideElementsByClassName = (className: string, show: boolean) => {
    const bookingNumberLinks = document.getElementsByClassName(className);
    // Loop through all elements and hide them
    for (let i = 0; i < bookingNumberLinks.length; i += 1) {
      const link = bookingNumberLinks[i] as HTMLElement; // Type assertion
      link.style.display = show ? 'block' : 'none';
    }
  };
  const doPdf = () => {
    const pdfHeader = document.getElementById('reportPdfHeader');
    if (pdfHeader) {
      // Make the pdfHeader visible
      pdfHeader.style.display = 'block';
    }
    // Hide the hyperlinks
    showOrHideElementsByClassName('bookingNumberLink', false);
    // Show the texts
    showOrHideElementsByClassName('bookingNumberText', true);

    const options = {
      filename: `Tilitysraportti_${dateToDateOnlyString(new Date())}.pdf`,
      margin: 0.5,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: {
        unit: 'in',
        orientation: 'landscape',
        putTotalPages: true,
        compress: true,
      },
      pagebreak: {
        avoid: 'tr',
      },
    };

    // const convertToPdf = () => {
    const content = contentRef.current;
    html2pdf()
      .set(options)
      .from(content)
      .toPdf()
      .get('pdf')
      .then(function (pdf) {
        const totalPages = pdf.internal.getNumberOfPages();
        for (let i = 1; i <= totalPages; i += 1) {
          pdf.setPage(i);
          pdf.setFontSize(10);
          pdf.setTextColor(100);
          pdf.text(
            `Sivu ${i} / ${totalPages}`,
            9.5,
            pdf.internal.pageSize.getHeight() - 8,
          );
        }
        // Hide the header again after PDF generation
        if (pdfHeader) {
          pdfHeader.style.display = 'none';
        }
        // Show the hyperlinks
        showOrHideElementsByClassName('bookingNumberLink', true);
        // Hide the texts
        showOrHideElementsByClassName('bookingNumberText', false);
      })
      .save()
      .catch(error => {
        console.error('Error generating PDF:', error);
        // Ensure the header is hidden in case of an error
        if (pdfHeader) {
          pdfHeader.style.display = 'none';
        }
      });
  };
  const isLoading = true;
  return (
    <>
      <Helmet>
        <title>Tilitysraportti</title>
      </Helmet>

      <Container className="container-content">
        <SectionHeader
          Breadcrumbs={['Tilitysraportti']}
          Title="Tilitysraportti"
        />

        <Card className="bg-white border shadow-none p-24 pb-8 mb-40">
          <SearchBarFilters DisplayOnDesktop FullWidth IsLoading={false}>
            <Row>
              <Col sm={6}>
                <Form.Group
                  controlId="feedbackStateFilterSelect"
                  className="mb-24"
                >
                  <Form.Label>Omistaja</Form.Label>
                  <OwnerQuickSearch
                    selectedOwnerOptionType={selectedOwnerOptionType}
                    Placeholder="Hae omistaja"
                    parentHandleSelectOwnerChange={handleSelectOwnerChange}
                  />
                </Form.Group>
              </Col>
              <Col sm={6}>
                {ownersAccommodationsLoadingStatus ===
                  LOADING_STATUS.LOADING && <Loader IsLoading={isLoading} />}
                {ownersAccommodationsLoadingStatus !==
                  LOADING_STATUS.LOADING && (
                  <Form.Group
                    controlId="feedbackStateFilterSelect"
                    className="mb-24"
                  >
                    <Form.Label>Kohde</Form.Label>

                    <Form.Select
                      id="ownersAccommodationSelect"
                      name="ownersAccommodationSelect"
                      value={accommodationDropdownSelectedAccommodation ?? 0} // controlled selection
                      onChange={e => accommodationDropdownChanged(e)}
                    >
                      <option value="0">Valitse kohde</option>
                      {ownersAccommodations
                        .slice() // clone so original array isn't mutated
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map(accommodation => (
                          <option
                            key={accommodation.id}
                            value={accommodation.id}
                          >
                            {accommodation.name},{' '}
                            {accommodation.accommodationNumber}
                          </option>
                        ))}
                    </Form.Select>
                  </Form.Group>
                )}
              </Col>
              <Col />
            </Row>
            <Row>
              <Col lg="5">
                <Form.Label htmlFor="selectStart">Valitse ajankohta</Form.Label>
                <div className="d-flex  gap-8 align-items-center">
                  <div className="w-100 position-relative">
                    <DatePicker
                      selected={startDate}
                      onChange={date =>
                        setAccommodationDropdownDateRange(date, undefined)
                      }
                      selectsStart
                      startDate={startDate}
                      endDate={endDate}
                      className="form-control"
                      dateFormat="dd.MM.yyyy"
                    />
                    <Button
                      variant="link"
                      className="pe-none position-absolute end-0 top-0"
                      id="button-addon2"
                    >
                      <span className="bi bi-calendar2-event" />
                    </Button>
                  </div>
                  <span className="bi bi-arrow-right" />
                  <div className="w-100 position-relative">
                    <DatePicker
                      selected={endDate}
                      onChange={date =>
                        setAccommodationDropdownDateRange(undefined, date)
                      }
                      selectsEnd
                      startDate={startDate}
                      endDate={endDate}
                      minDate={startDate}
                      className="form-control"
                      dateFormat="dd.MM.yyyy"
                    />
                    <Button
                      variant="link"
                      className="pe-none position-absolute end-0 top-0"
                      id="button-addon2"
                    >
                      <span className="bi bi-calendar2-event" />
                    </Button>
                  </div>
                </div>
              </Col>
              <Col className="d-flex align-items-end">
                <Button
                  onClick={() => doSearch()}
                  variant="primary"
                  disabled={
                    selectedOwnerOptionType === undefined ||
                    selectedOwnerOptionType === null
                  }
                  className="mt-auto w-100"
                >
                  Tee raportti
                </Button>
              </Col>
              <Col className="d-flex align-items-end">
                <Button
                  variant="primary"
                  className="mt-auto w-100"
                  onClick={() => doPdf()}
                  disabled={
                    settlementReport === null ||
                    settlementReport === undefined ||
                    settlementReport.settlementReportItems === null ||
                    settlementReport.settlementReportItems === undefined ||
                    settlementReport.settlementReportItems.length === 0
                  }
                >
                  Vie Pdf
                </Button>
              </Col>
            </Row>
            <Row className="d-print-none gy-16">
              <Col lg="5">
                <br />
                <div className="form-check">
                  <input
                    checked={noBookingsAfterEndDate}
                    onChange={noBookingsAfterEndDateHandler}
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="flexCheckDefault"
                  />
                  <Form.Label
                    className="form-check-label"
                    htmlFor="flexCheckDefault"
                  >
                    Rajaa varaukset päättymispäivän mukaan
                  </Form.Label>
                </div>
              </Col>
            </Row>
          </SearchBarFilters>

          {settlementReportLoadingStatus !== LOADING_STATUS.IDLE && (
            <p style={{ height: 32 }}>
              Ladataan raporttia...
              <Spinner animation="border" />
            </p>
          )}

          <div ref={contentRef}>
            <div id="reportPdfHeader" style={{ display: 'none' }}>
              <Row>
                <Col>
                  <div>
                    <h5>LOMARENGAS OY</h5>
                    <br />
                    Siltasaarenkatu 8-10
                    <br />
                    FI-00530 Helsinki
                    <br />
                    Puh 0306 502 502
                    <br />
                  </div>
                </Col>
                <Col>
                  <p>TILITYSRAPORTTI {dateToDateOnlyString(new Date())}</p>
                  {selectedOwnerOptionType != null &&
                    `Omistaja: ${selectedOwnerOptionType.label}`}
                </Col>
              </Row>
              <br />
              {/* }
                <br />
                {ownerInfo != null && ownerInfo.postStreetAddress}
                <br />
                {ownerInfo != null &&
                  `${ownerInfo.postCode} ${ownerInfo.postCity}`}

              </p>
               { */}
              <p>
                <h5>
                  Kausi: {dateToDateOnlyString(dayPickerSelectedRange.from)} -{' '}
                  {dateToDateOnlyString(dayPickerSelectedRange.to)}
                </h5>
              </p>
            </div>
            <table className="table mt-16 mb-0">
              <thead>
                <tr>
                  <th scope="col" style={{ minWidth: '80px' }}>
                    Varaus
                  </th>
                  <th scope="col">Kohde </th>
                  <th scope="col">Alkupvm </th>
                  <th scope="col">Loppupvm </th>
                  <th scope="col">Asiakas </th>
                  <th scope="col">Tilitys </th>
                  <th scope="col">Alv % </th>
                  <th scope="col">Alv osuus</th>
                </tr>
              </thead>
              <tbody className="no-border-tbody">{renderReport()}</tbody>
              <tfoot>
                <tr>
                  <th scope="col" />
                  <th scope="col" />
                  <th scope="col" />
                  <th scope="col" />
                  <th scope="col">Yhteensä</th>
                  <th scope="col">
                    {formattedAmountWithEuroCurrency(totalSettlementValue)}
                  </th>
                  <th scope="col" />
                  <th scope="col" />
                </tr>
              </tfoot>
            </table>
            {settlementReportVats.settlementReportVatItems.length > 0 && (
              <>
                <br />
                <div id="reportVatSpecificationDiv" style={{ width: '500px' }}>
                  <h5>Arvonlisäveroerittely</h5>
                  <table
                    className="table table-data"
                    style={{ textAlign: 'right' }}
                  >
                    <thead>
                      <th scope="col">Alv %</th>
                      <th scope="col">Veroton</th>
                      <th scope="col">Vero</th>
                      <th scope="col">Yhteensä</th>
                    </thead>
                    <tbody>{renderVats()}</tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </Card>
      </Container>
    </>
  );
}

export default SettlementReport;
